import { map, filter } from 'lodash'
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Currencies } from '../../../common/models'
import { OrderPaymentStatus } from '../../../common/models/orders'
import { IColumn, Table } from '../../../core/components/Table'
import { formatDateTime } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { IClientOrders, clientsOrders } from '../../../core/services/clients'
import { getCurrencies } from '../../../core/services/festival-settings'
import { Pagination } from '../Pagination'
import { Checkbox } from '@/components/ui'
import { Icon } from '@/common/components/Icon'

interface Props {
  clientId: string
  maxHeight?: string
}

export const ClientOrdersTable: FC<Props> = ({ clientId, maxHeight }) => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams({
    defaultSortBy: 'id',
  })

  const [orders, setOrders] = useState<IClientOrders[]>([])
  const { festivalName, editionId } = useParams()
  const navigate = useNavigate()

  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])

  const handleCurrencyConversion = (
    orders: IClientOrders[],
    currencies: Currencies,
  ) => {
    const result = map(orders, (order) => {
      if (order.price.currency === currencies.defaultCurrency.name) {
        return order
      } else {
        const currency = filter(
          currencies.currencies,
          (c) => c.name === order.price.currency,
        )
        if (currency.length < 1) {
          const temp = order
          temp.price.currency = currencies.defaultCurrency.name
          return temp
        }
        const temp = order
        temp.price.value = Number(
          (order.price.value * currency[0].exchangeRate).toFixed(2),
        )
        return temp
      }
    })
    return result
  }

  const renderPaymentStatus = (status: OrderPaymentStatus) => {
    switch (status) {
      case 'PAID':
      case 'REDEEMED':
      case 'PAYMENT_NOT_NEEDED':
        return (
          <Icon
            icon="success"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-accent-2"
          />
        )
      case 'FAILED':
      case 'INSTALLMENTS_FAILED':
      case 'CHECKIN_FAILED':
      case 'CANCELED':
        return (
          <Icon
            icon="failed"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-destructive"
          />
        )
      case 'REFUNDED':
        return (
          <Icon
            icon="return"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-primary"
          />
        )
      case 'PAYMENT_PENDING':
      case 'INSTALLMENTS_ON_GOING':
        return (
          <Icon
            icon="waiting"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-warning"
          />
        )

      default:
        return (
          <Icon
            icon="failed"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            className="text-destructive"
          />
        )
    }
  }

  const getOrders = useCallback(async () => {
    try {
      const currencyCall = await getCurrencies()
      const { data, status } = await clientsOrders(clientId, {
        page,
        size,
        sortBy,
      })
      if (status === 200) {
        setOrders(handleCurrencyConversion(data.content, currencyCall.data))
        setTotalElemenets(data.totalElements)
      }
    } catch (err) {
      console.log(err)
    }
  }, [page, size, sortBy, clientId])

  useEffect(() => {
    getOrders()
  }, [clientId, getOrders])

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck(orders.map((clt) => clt.id))
    if (isCheckAll) {
      setIsCheck([])
    }
  }

  const handleClick = (id: string, checked: boolean) => {
    setIsCheck([...isCheck, id])
    if (!checked) {
      setIsCheck(isCheck.filter((item) => item !== id))
    } else {
      setIsCheck([...isCheck, id])
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      setSortBy('id')
    } else {
      setSortBy(sort)
    }
  }

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),

      width: '16px',
    },
    {
      title: 'Id',
      accessor: 'id',
      sortable: true,
      render: (row) => (
        <div className="flex flex-col">
          <span>{row.id}</span>
          <span>Order Number: {row.displayableOrderNumber}</span>
          <span>From: {row.agent !== null ? row.agent : 'N/A'}</span>
        </div>
      ),
    },
    {
      title: 'Barcodes',
      accessor: 'barcodes',
      render: (row) => (
        <div className="flex flex-col">
          {map(row.barcodes, (barcode, index) => (
            <span key={`${barcode}-${index}`}>{barcode}</span>
          ))}
        </div>
      ),
    },
    {
      title: 'Buyer',
      accessor: 'buyer',
      render: (row) => (
        <div>
          <p>{row.buyer.name}</p>
          <p>{row.buyer.phoneNumber}</p>
          <p>{row.buyer.email}</p>
        </div>
      ),
    },
    {
      title: 'Price',
      accessor: 'price',
      render: (row) => (
        <div>
          <p>
            {row.price.value} {row.price.currency}
          </p>
          <p>{row.price.runa} RUNA</p>
        </div>
      ),
    },
    {
      title: 'Purchase Date',
      accessor: 'purchaseDate',
      render: (row) => formatDateTime(row.purchaseDate),
    },
    {
      title: 'Payment Status',
      accessor: 'paymentStatus',
      render: (row) => (
        <div className="flex items-center gap-1">
          {renderPaymentStatus(row.paymentStatus)}
          <span>{row.paymentStatus.replaceAll('_', ' ')}</span>
        </div>
      ),
    },
    {
      title: 'Payment',
      accessor: 'payment',
      render: (row) => (row.payFullPrice ? 'Full Payment' : 'Monthly Payments'),
    },
    {
      title: 'View',
      accessor: '',
      render: (row) => (
        <div
          onClick={() => {
            navigate(`/orders/${festivalName}/${editionId}/${row.id}`)
          }}
          className="view"
          style={{ cursor: 'pointer' }}
        >
          View
        </div>
      ),
    },
  ]

  return (
    <>
      <Table
        isCheck={isCheck}
        columns={columns}
        handleSortBy={handleSortBy}
        data={orders}
        maxHeight={maxHeight}
      />
      <Pagination
        className="static"
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
        nested
      />
    </>
  )
}
